import {
    DeleteOutlined,
    DownloadOutlined,
    ExclamationCircleOutlined,
    FileOutlined,
    FolderOpenOutlined,
} from "@ant-design/icons";
import {
    Button,
    DatePicker,
    message,
    Modal,
    Pagination,
    Space,
    Table,
    Tooltip,
} from "antd";
import Input from "antd/es/input/Input";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import { getTitleFromPath } from "../../libs/utilities/helpers/getTitle";
import { formatDateToYYYYMMDD } from "../../libs/utilities/helpers/dateFormatter";
import { calculatePageSize } from "../../libs/utilities/helpers/paginationHelper";
import {
    uploadClaimFiles,
    getClaimReportList,
    downloadClaimReport,
    deleteClaimReport,
    triggerExcelDownload,
} from "../../apis/ClaimInvestigatorAPI";

import UploadMedia, { trimFileName } from "./UploadMedia";
import * as XLSX from "xlsx";

const { RangePicker } = DatePicker;

// Groups now use numeric naming: "Group 1", "Group 2", etc.
const MAX_FILES_PER_BATCH = 50;
const MAX_ROWS_PER_PAGE = 4;

// ─────────────────────────────────────────────────────────────────────────────
// parseExcelBase64 — handles 2-row header structure
// ─────────────────────────────────────────────────────────────────────────────

const REQUIRED_COLUMNS = [
    { label: "Source file", match: ["source_file", "source file"] },
    { label: "Policy No", match: ["policy no", "policy number", "policy_no", "policy_number"] },
    { label: "Assessment recommendation", match: ["pre-claims assessment recommendation", "pre_claims_assessment_recommendation", "pre-claims assessment", "preclaims assessment recommendation"] },
    { label: "Basis for decision", match: ["basis for decision", "basis_for_decision"] },
    { label: "Report Outcome", match: ["report outcome", "report_outcome"] },
];

const parseExcelBase64 = (base64) => {
    const binary = atob(base64);
    const buf = new ArrayBuffer(binary.length);
    const u8 = new Uint8Array(buf);
    for (let i = 0; i < binary.length; i++) u8[i] = binary.charCodeAt(i);

    const wb = XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];

    const allRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

    if (!allRows || allRows.length < 2) {
        return { data: [], columns: [] };
    }

    const headerRow = allRows[1].map((h) => (h ?? "").toString().trim());
    const dataRows = allRows.slice(2).filter((row) =>
        row.some((cell) => cell !== "" && cell !== null && cell !== undefined)
    );

    if (dataRows.length === 0) {
        return { data: [], columns: [] };
    }

    const norm = (s) => (s ?? "").toString().trim().toLowerCase();

    const colMap = REQUIRED_COLUMNS.map(({ label, match }) => {
        const idx = headerRow.findIndex((h) => match.includes(norm(h)));
        return { label, idx };
    });

    const columns = colMap
        .filter(({ idx }) => idx !== -1)
        .map(({ label }) => ({
            title: label,
            dataIndex: label,
            key: label,
            ellipsis: true,
            width: label === "Assessment recommendation" ? 260 : 180,
            render: (v) => {
                if (!v || v === "—") {
                    return <span style={{ color: "#a0aec0", fontStyle: "italic" }}>—</span>;
                }
                const full = String(v);
                const words = full.split(/\s+/);
                const trimmed = words.length > 10
                    ? words.slice(0, 10).join(" ") + "…"
                    : full;
                return (
                    <Tooltip title={full} placement="topLeft">
                        <span style={{ cursor: "default" }}>{trimmed}</span>
                    </Tooltip>
                );
            },
        }));

    const data = dataRows.map((row, i) => {
        const out = { _rowKey: i };
        colMap.forEach(({ label, idx }) => {
            if (idx === -1) { out[label] = "—"; return; }
            const val = row[idx];
            out[label] = val === "" || val === null || val === undefined ? "—" : val;
        });
        return out;
    });

    return { data, columns };
};

// ─────────────────────────────────────────────────────────────
// InfoDialog
// ─────────────────────────────────────────────────────────────
const InfoDialog = ({ open, title, onConfirm, onCancel }) => {
    if (!open) return null;
    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,.45)",
                zIndex: 1050,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <div
                style={{
                    background: "#fff",
                    borderRadius: 8,
                    padding: "24px 32px",
                    minWidth: 320,
                    boxShadow: "0 8px 32px rgba(0,0,0,.18)",
                }}
            >
                <p style={{ fontWeight: 600, fontSize: 16, marginBottom: 16 }}>
                    {title || "Are you sure you want to delete?"}
                </p>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button type="primary" danger onClick={onConfirm}>
                        Confirm
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// ExpandedRow
// ─────────────────────────────────────────────────────────────
const ExpandedRow = ({ record, downloadCache, previewDataCache, dispatch }) => {
    const [fetchState, setFetchState] = useState(
        record.is_processed ? "loading" : "idle"
    );
    const [tableData, setTableData] = useState([]);
    const [tableCols, setTableCols] = useState([]);
    const didFetch = useRef(false);

    useEffect(() => {
        if (!record.is_processed) return;
        if (didFetch.current) return;
        didFetch.current = true;

        const docId =
            record.group_doc_ids?.length
                ? record.group_doc_ids[0]
                : record.document_id;

        if (!docId || typeof docId !== "string" || docId.trim() === "") {
            console.error("[ExpandedRow] Could not resolve a valid document ID:", record);
            setFetchState("error");
            return;
        }

        (async () => {
            try {
                let base64 = downloadCache.current[docId];

                if (!base64) {
                    const action = await dispatch(
                        downloadClaimReport({ _id: docId.trim() })
                    );
                    if (action.error) {
                        throw new Error(action.payload?.message ?? "API returned an error");
                    }
                    const payload = action.payload;
                    base64 = payload?.data?.data ?? payload?.data ?? null;
                    if (!base64) throw new Error("API returned an empty payload");
                    downloadCache.current[docId] = base64;
                }

                const { data, columns } = parseExcelBase64(base64);
                previewDataCache.current[docId] = data;

                const expectedRows = Math.max((record.file_names ?? []).length, 1);
                setTableData(data.slice(0, expectedRows));
                setTableCols(columns);
                setFetchState("done");
            } catch (err) {
                console.error("[ExpandedRow] preview fetch failed:", err);
                setFetchState("error");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fileNames = record.file_names ?? [];

    const PREVIEW_ROW_HEIGHT = 34;
    const MAX_VISIBLE_ROWS = 4;
    const scrollY = PREVIEW_ROW_HEIGHT * MAX_VISIBLE_ROWS;

    return (
        <div style={{
            padding: "8px 16px 10px 40px",
            // Fix: use same background as table rows, no contrast break
            background: "#ffffff",
            borderTop: "1px solid #e2e8f0",
            /* Fix: prevent content from blowing out table width on small screens */
            width: "100%",
            boxSizing: "border-box",
            overflow: "hidden",
        }}>

            {/* Source file chips */}
            {fileNames.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                    <div style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: 4,
                    }}>
                        Source files
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {fileNames.map((name, i) => (
                            <span key={i} style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                background: "rgb(203 235 255)",
                                border: "1px solid #e2e8f0",
                                borderRadius: "12px",
                                padding: "4px 10px",
                                fontSize: 12,
                                color: "#4096ff",
                                maxWidth: 280,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}>
                                <FileOutlined style={{ fontSize: 12, color: "#4096ff", flexShrink: 0 }} />
                                <span title={name}>{trimFileName(name, 50)}</span>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Preview table */}
            {record.is_processed && (
                <div>
                    <div style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: 4,
                    }}>
                        Report preview
                    </div>

                    {fetchState === "loading" && (
                        <div style={{ padding: "10px 0", color: "#94a3b8", fontSize: 12 }}>
                            Loading report…
                        </div>
                    )}

                    {fetchState === "error" && (
                        <div style={{ padding: "8px 0", color: "#b91c1c", fontSize: 12 }}>
                            Failed to load preview. Use the download button instead.
                        </div>
                    )}

                    {fetchState === "done" && tableCols.length > 0 && (
                        <div style={{ overflow: "hidden" }}>
                            <Table
                                columns={tableCols}
                                dataSource={tableData}
                                pagination={false}
                                // scroll={{ x: true, y: scrollY }}
                                rowKey={(row) => row._rowKey}
                                size="small"
                                 tableLayout="auto" 
                                style={{ fontSize: 12 }}
                                className="ci-preview-table"
                                components={{
                                    header: {
                                        cell: (props) => (
                                            <th
                                                {...props}
                                                style={{
                                                    ...props.style,
                                                    padding: "4px 10px",
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    background: "#f1f5f9",
                                                    color: "#374151",
                                                    whiteSpace: "nowrap",
                                                }}
                                            />
                                        ),
                                    },
                                    body: {
                                        cell: (props) => (
                                            <td
                                                {...props}
                                                style={{
                                                    ...props.style,
                                                    padding: "4px 10px",
                                                    fontSize: 14,
                                                    color: "#1e293b",
                                                    // Ensure inner table cells have white bg
                                                    background: "#ffffff",
                                                }}
                                            />
                                        ),
                                    },
                                }}
                            />
                        </div>
                    )}

                    {fetchState === "done" && tableCols.length === 0 && (
                        <div style={{ color: "#94a3b8", fontSize: 12, padding: "6px 0" }}>
                            No matching columns found in report.
                        </div>
                    )}
                </div>
            )}

            {!record.is_processed && fileNames.length === 0 && (
                <div style={{ color: "#94a3b8", fontSize: 12 }}>
                    No details available yet.
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// ClaimInvestigator
// ─────────────────────────────────────────────────────────────
const ClaimInvestigator = () => {
    const dispatch = useDispatch();
    const location = useLocation();

    const [searchText, setSearchText] = useState("");
    const [fileList, setFileList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(false);

    const [allReport, setAllReport] = useState([]);
    const [filteredReport, setFilteredReport] = useState([]);
    const [filteredReportWithoutSort, setFilteredReportWithoutSort] = useState([]);

    const [downloadLoading, setDownloadLoading] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState([]);

    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        title: "",
        id: null,
        groupName: "",
    });

    const [tableSorter, setTableSorter] = useState(null);
    // Fixed: max 4 rows per page
    const [pagination, setPagination] = useState({ current: 1, pageSize: MAX_ROWS_PER_PAGE, total: 0 });
    const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });

    const [filePreviewOpen, setFilePreviewOpen] = useState(false);
    const [filePreviewNames, setFilePreviewNames] = useState([]);
    const [filePreviewTitle, setFilePreviewTitle] = useState("");

    const downloadCache = useRef({});
    const previewDataCache = useRef({});
    const [expandVersion, setExpandVersion] = useState({});
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);

    const sessionGroupCounter = useRef(0);
    const fileInputRef = useRef(null);

    const dateFormat = "YYYY-MM-DD";
    const defaultDateRange = [dayjs().subtract(6, "days"), dayjs()];


    useEffect(() => {
        const startDate = formatDateToYYYYMMDD(dayjs().subtract(6, "days"));
        const endDate = formatDateToYYYYMMDD(dayjs());
        setDateRange({ startDate, endDate });
        getReport(startDate, endDate);
        // No resize listener needed — pageSize is fixed at MAX_ROWS_PER_PAGE
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const buildDisplayRows = useCallback((documents) => {
        const groupMap = {};
        const rows = [];

        documents.forEach((item, index) => {
            const itemId = (item._id ?? "").toString();
            const gName = (item.group_name ?? "").trim();
            const dateKey = formatDateToYYYYMMDD(item.createdAt);

            if (gName) {
                const mapKey = `${gName}__${dateKey}`;
                if (groupMap[mapKey]) {
                    const existing = groupMap[mapKey];
                    const incoming = item.file_names?.length
                        ? item.file_names
                        : [item.document_name];
                    existing.file_names = [
                        ...new Set([...existing.file_names, ...incoming]),
                    ];
                    existing.group_doc_ids.push(itemId);
                } else {
                    const row = {
                        id: `group-${mapKey}-${index}`,
                        document_id: itemId,
                        group_doc_ids: [itemId],
                        document_name: item.document_name,
                        group_name: gName,
                        file_names: item.file_names?.length
                            ? [...item.file_names]
                            : [item.document_name],
                        is_processed: item.is_processed,
                        createdAt: dateKey,
                        updatedAt: item.updatedAt,
                    };
                    groupMap[mapKey] = row;
                    rows.push(row);
                }
            } else {
                rows.push({
                    id: index,
                    document_id: itemId,
                    group_doc_ids: [],
                    document_name: item.document_name,
                    group_name: "",
                    file_names: item.file_names ?? [],
                    is_processed: item.is_processed,
                    createdAt: dateKey,
                    updatedAt: item.updatedAt,
                });
            }
        });

        return rows;
    }, []);

    const getReport = async (startDate, endDate) => {
        setFetchLoading(true);
        setExpandedRowKeys([]);
        setExpandVersion({});
        downloadCache.current = {};

        try {
            const response = await dispatch(getClaimReportList({ startDate, endDate }));
            if (!response.error) {
                setSearchText("");
                const sorted = (response.payload.data.documents ?? []).sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                );
                const rows = buildDisplayRows(sorted);
                setPagination((prev) => ({ ...prev, current: 1, pageSize: MAX_ROWS_PER_PAGE, total: rows.length }));
                setAllReport(rows);
                setFilteredReport(rows);
                setFilteredReportWithoutSort([...rows]);
            } else {
                message.destroy();
                message.error("Failed to load reports. Please refresh the page.");
            }
        } catch {
            message.destroy();
            message.error("An unexpected error occurred while loading reports.");
        } finally {
            setFetchLoading(false);
        }
    };

    const handleDateFilter = (dates, dateStrings) => {
        if (!dates) return;
        const startDate = formatDateToYYYYMMDD(dateStrings[0]);
        const endDate = formatDateToYYYYMMDD(dateStrings[1]);
        setDateRange({ startDate, endDate });
        getReport(startDate, endDate);
    };

    const disabledDate = (current) => {
        const eod = new Date();
        eod.setHours(23, 59, 59, 999);
        return current && current > eod;
    };

    const sortTableData = (_, _filters, sorter) => {
        setTableSorter(sorter);
        if (sorter.columnKey === "document_name") {
            if (!sorter.order) {
                setFilteredReport([...filteredReportWithoutSort]);
                return;
            }
            setFilteredReport(
                [...filteredReport].sort((a, b) =>
                    sorter.order === "ascend"
                        ? a.document_name.localeCompare(b.document_name)
                        : b.document_name.localeCompare(a.document_name)
                )
            );
        }
    };

    const handlePageChange = (page, pageSize) =>
        setPagination((prev) => ({ ...prev, current: page, pageSize }));

    const startIdx = (pagination.current - 1) * pagination.pageSize;
    const displayData = filteredReport.slice(startIdx, startIdx + pagination.pageSize);

    const startDocumentDelete = (record) => {
        const isGroup = !!record.group_name;
        setDeleteDialog({
            open: true,
            title: isGroup
                ? `Delete all files in "${record.group_name}"?`
                : "Are you sure you want to delete this file?",
            id: record.document_id,
            groupName: record.group_name,
        });
    };

    const removeRowsFromState = useCallback((docId, groupName) => {
        const keep = groupName
            ? (row) => (row.group_name ?? "") !== groupName
            : (row) => row.document_id !== docId;

        if (groupName) {
            Object.keys(downloadCache.current).forEach((cachedId) => {
                if (cachedId === docId) delete downloadCache.current[cachedId];
            });
        } else {
            delete downloadCache.current[docId];
        }

        setAllReport((prev) => prev.filter(keep));

        setFilteredReport((prev) => {
            const next = prev.filter(keep);
            setPagination((p) => ({
                ...p,
                current: Math.min(
                    p.current,
                    Math.max(1, Math.ceil(next.length / p.pageSize))
                ),
                total: next.length,
            }));
            return next;
        });

        setFilteredReportWithoutSort((prev) => prev.filter(keep));

        setExpandedRowKeys((prev) =>
            prev.filter((k) => {
                if (groupName) return !String(k).includes(`group-${groupName}__`);
                return k !== docId;
            })
        );
    }, []);

    const confirmDocumentDelete = async () => {
        const { id, groupName } = deleteDialog;
        try {
            const res = await dispatch(
                deleteClaimReport({
                    _id: id,
                    ...(groupName ? { group_name: groupName } : {}),
                })
            );

            if (!res.error) {
                message.destroy();
                message.success(
                    groupName
                        ? `All files in "${groupName}" deleted successfully`
                        : "File deleted successfully"
                );
                setDeleteDialog({ open: false, title: "", id: null, groupName: "" });

                if (fileInputRef.current) fileInputRef.current.value = "";
                setFileList([]);

                removeRowsFromState(id, groupName);
                await getReport(dateRange.startDate, dateRange.endDate);
            } else {
                message.destroy();
                message.error(res.payload?.message ?? "Delete failed. Please try again.");
            }
        } catch {
            message.destroy();
            message.warning("Something went wrong!");
        }
    };

    const cancelDocumentDelete = () => {
        setDeleteDialog({ open: false, title: "", id: null, groupName: "" });
    };

    const handleDownloadReport = async (record) => {
        const docId =
            record.group_doc_ids?.length
                ? record.group_doc_ids[0]
                : record.document_id;

        if (!docId || typeof docId !== "string" || docId.trim() === "") {
            message.destroy();
            message.error("Invalid report ID.");
            return;
        }

        setSelectedDocument((prev) => [...prev, docId]);
        setDownloadLoading(true);

        try {
            let base64 = downloadCache.current[docId];

            if (!base64) {
                const res = await dispatch(
                    downloadClaimReport({ _id: docId.trim() })
                );

                if (res.error) throw new Error(res.payload?.message);

                const payload = res.payload;
                base64 =
                    payload?.data?.data ??
                    payload?.data ??
                    null;

                if (!base64) throw new Error("Empty payload from server");

                downloadCache.current[docId] = base64;
            }

            triggerExcelDownload(base64, `claim_${Date.now()}.xlsx`);
        } catch (e) {
            console.error("[handleDownloadReport]", e);
            message.destroy();
            message.error("Failed to download report.");
        } finally {
            setSelectedDocument((prev) => prev.filter((d) => d !== docId));
            setDownloadLoading(false);
        }
    };

    const openFilePreview = (record, e) => {
        e?.stopPropagation();
        setFilePreviewNames(record.file_names ?? []);
        setFilePreviewTitle(
            `Files in ${record.group_name || record.document_name || "Group"}`
        );
        setFilePreviewOpen(true);
    };

    const hasInvalidChars = (v) => /[<>/"'`;()&*#$%!^?]/.test(v);

    /**
     * Recursive search: matches rows by name/group OR by any value
     * in the nested preview table data (if already cached).
     * Also recursively searches nested file_names array.
     */
    const handleSearch = (e) => {
        const s = e.target.value;
        if (hasInvalidChars(s)) {
            message.destroy();
            message.error("Special characters are not allowed");
            return;
        }
        setSearchText(s);
        const term = s.trim().toLowerCase();

        if (!term) {
            // Reset to full list
            setPagination((prev) => ({ ...prev, current: 1, total: allReport.length }));
            setFilteredReportWithoutSort([...allReport]);
            setFilteredReport(allReport);
            return;
        }

        const filtered = allReport.filter((item) => {
            // 1. Match document name
            if (item.document_name.toLowerCase().includes(term)) return true;

            // 2. Match group name
            if ((item.group_name ?? "").toLowerCase().includes(term)) return true;

            // 3. Recursively match file_names array
            if ((item.file_names ?? []).some((fn) => fn.toLowerCase().includes(term))) return true;

            // 4. Match inside expanded/cached preview table data (recursive cell search)
            const docId =
                item.group_doc_ids?.length
                    ? item.group_doc_ids[0]
                    : item.document_id;

            const previewRows = previewDataCache.current[docId];
            if (!previewRows) return false;

            // Recursively search all values in each preview row (including nested objects/arrays)
            const deepSearch = (value) => {
                if (value === null || value === undefined) return false;
                if (typeof value === "string") return value.toLowerCase().includes(term);
                if (typeof value === "number") return String(value).toLowerCase().includes(term);
                if (Array.isArray(value)) return value.some(deepSearch);
                if (typeof value === "object") return Object.values(value).some(deepSearch);
                return false;
            };

            return previewRows.some((row) =>
                Object.entries(row).some(([key, value]) => {
                    if (key === "_rowKey") return false; // skip internal key
                    return deepSearch(value);
                })
            );
        });

        setPagination((prev) => ({ ...prev, current: 1, total: filtered.length }));
        setFilteredReportWithoutSort([...filtered]);

        if (tableSorter?.columnKey === "document_name" && tableSorter.order) {
            setFilteredReport(
                [...filtered].sort((a, b) =>
                    tableSorter.order === "ascend"
                        ? a.document_name.localeCompare(b.document_name)
                        : b.document_name.localeCompare(a.document_name)
                )
            );
        } else {
            setFilteredReport(filtered);
        }
    };

    /**
     * Numeric group label: "Group 1", "Group 2", etc.
     * Uses sessionGroupCounter ref, increments each upload session.
     */
    const getNextSessionGroupLabel = () => {
        sessionGroupCounter.current += 1;
        return `Group ${sessionGroupCounter.current}`;
    };

    const generateReport = async () => {
        if (fileList.length === 0) {
            message.destroy();
            message.warning("Please upload at least one file");
            return;
        }

        if (fileList.length > MAX_FILES_PER_BATCH) {
            message.destroy();
            message.error(`You can upload a maximum of ${MAX_FILES_PER_BATCH} files at once.`);
            return;
        }

        setLoading(true);
        try {
            const rawFiles = fileList.map((f) =>
                f instanceof File ? f : f.originFileObj
            );
            const isGrouped = rawFiles.length > 1;
            const groupLabel = isGrouped ? getNextSessionGroupLabel() : "";

            const response = await dispatch(
                uploadClaimFiles({ files: rawFiles, groupName: groupLabel })
            );

            if (!response.error) {
                message.destroy();
                message.success(
                    isGrouped
                        ? `${rawFiles.length} files uploaded as "${groupLabel}".`
                        : "File processed successfully. Expand the row to preview."
                );
                setFileList([]);
                if (fileInputRef.current) fileInputRef.current.value = "";
                await getReport(dateRange.startDate, dateRange.endDate);
            } else {
                message.destroy();
                message.error(
                    response.payload?.message ?? "Upload failed. Please check your files."
                );
            }
        } catch {
            message.destroy();
            message.error("An unexpected error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleExpand = (expanded, record) => {
        if (expanded) {
            setExpandVersion((prev) => ({
                ...prev,
                [record.id]: (prev[record.id] ?? 0) + 1,
            }));
            // Allow ONLY one expanded row at a time
            setExpandedRowKeys([record.id]);
        } else {
            setExpandedRowKeys([]);
        }
    };

    const columns = [
        {
            title: "Name",
            dataIndex: "document_name",
            key: "document_name",
            sorter: () => { },
            width: 130,
            render: (name, record) => {
                const label = record.group_name || name;
                return (
                    <span title={label} style={{ fontWeight: 500 }}>
                        {trimFileName(label, 100)}
                    </span>
                );
            },
        },
        {
            title: "Files",
            key: "file_names",
            width: 70,
            render: (_, record) => {
                const count = (record.file_names ?? []).length;
                if (!count) return "—";
                return (
                    <Button
                        type="text"
                        size="small"
                        icon={<FolderOpenOutlined />}
                        style={{ fontSize: 12, color: "#378ADD" }}
                        onClick={(e) => openFilePreview(record, e)}
                    >
                        {count}
                    </Button>
                );
            },
        },
        {
            title: "Date",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 110,
        },
        {
            title: "Status",
            dataIndex: "is_processed",
            key: "is_processed",
            width: 120,
            render: (isProcessed) =>
                isProcessed ? (
                    <span className="ci-status ci-status--done">
                        <span className="ci-status__dot" /> Completed
                    </span>
                ) : (
                    <span className="ci-status ci-status--pending">
                        <span className="ci-status__dot" /> In Progress
                    </span>
                ),
        },
        {
            title: "Action",
            key: "action",
            width: 120,
            render: (_, record) => (
                <Space size="small">
                    {record.is_processed ? (
                        <Tooltip title="Download report">
                            <Button
                                type="text"
                                loading={
                                    selectedDocument.includes(record.document_id) &&
                                    downloadLoading
                                }
                                onClick={() => handleDownloadReport(record)}
                            >
                                <DownloadOutlined />
                            </Button>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Report not yet generated">
                            <Button type="text" disabled>
                                <ExclamationCircleOutlined style={{ color: "#f97316" }} />
                            </Button>
                        </Tooltip>
                    )}
                    <Tooltip title={record.group_name ? "Delete group" : "Delete file"}>
                        <Button type="text" onClick={() => startDocumentDelete(record)}>
                            <DeleteOutlined />
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div className="ci-page">
            <div className="heading-div">
                <p className="text5">{getTitleFromPath(location?.pathname)}</p>
            </div>

            <InfoDialog
                open={deleteDialog.open}
                title={deleteDialog.title}
                onConfirm={confirmDocumentDelete}
                onCancel={cancelDocumentDelete}
            />

            <div className="early-claim-container">
                <div className="file-table">
                    <div className="table-actions">
                        <Input
                            size="middle"
                            value={searchText}
                            onChange={handleSearch}
                            placeholder="Search by name or group"
                        />
                        <div style={{ display: "flex" }}>
                            <RangePicker
                                className="file-datepicker"
                                onChange={handleDateFilter}
                                disabledDate={disabledDate}
                                defaultValue={defaultDateRange}
                                format={dateFormat}
                                allowClear={false}
                            />
                        </div>
                    </div>

                    {/* Table with contained scroll — no global page scroll */}
                    <div className="ci-table-scroll-wrapper">
                        <Table
                            columns={columns}
                            dataSource={displayData}
                            loading={fetchLoading}
                            pagination={false}
                            rowKey={(r) => r.id}
                            onChange={sortTableData}
                            expandable={{
                                expandedRowKeys,
                                onExpand: handleExpand,
                                expandedRowRender: (record) => (
                                    <ExpandedRow
                                        key={`er-${record.id}-v${expandVersion[record.id]}`}
                                        record={record}
                                        downloadCache={downloadCache}
                                        previewDataCache={previewDataCache}
                                        dispatch={dispatch}
                                    />
                                ),
                                rowExpandable: () => true,
                            }}
                            // No inline overflow — handled by wrapper
                            style={{ width: "100%" }}
                        />
                    </div>

                    <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={+pagination.total}
                        onChange={handlePageChange}
                        style={{ marginTop: "12px", justifyContent: "flex-end" }}
                    />
                </div>

                <UploadMedia
                    fileList={fileList}
                    handleFileSelect={setFileList}
                    onSubmit={generateReport}
                    disabled={loading}
                    fileInputRef={fileInputRef}
                    maxFiles={MAX_FILES_PER_BATCH}
                />
            </div>

            <Modal
                open={filePreviewOpen}
                onCancel={() => setFilePreviewOpen(false)}
                footer={null}
                title={filePreviewTitle}
                width={560}
                destroyOnClose
                styles={{
                    body: {
                        maxHeight: "60vh",
                        overflowY: "auto",
                        padding: "16px 0 4px",
                    },
                }}
            >
                <div className="ci-preview-list">
                    {filePreviewNames.length === 0 ? (
                        <div style={{ color: "#718096", fontSize: 13, textAlign: "center", padding: "20px 0" }}>
                            No file names available.
                        </div>
                    ) : (
                        filePreviewNames.map((name, i) => (
                            <div key={i} className="ci-preview-file-row">
                                <span className="ci-preview-file-row__index">{i + 1}</span>
                                <span className="ci-preview-file-row__name" title={name}>
                                    {trimFileName(name, 100)}
                                </span>
                                <span className="ci-preview-file-row__ext">PDF</span>
                            </div>
                        ))
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default ClaimInvestigator;